import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Flex,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import type { DirectChat } from '@th-types/inbox-reviews.type';
import type { TableSelectionProps } from '@hooks/useTableSelection';
import { formatLocalDate, formatTimeOrDateNow } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { isAnnouncementChat } from '@constants/directChat';
import { TABLE_HEADER_STYLE } from '@components/InboxReviews/tableStyles';
import MailIcon from '@components/InboxReviews/MailIcon';
import InboxMessageListItem from '@components/InboxReviews/InboxMessageListItem';

interface DirectMessagesTableProps {
  directMessages: DirectChat[];
  onOpenChat?: (chat: DirectChat) => void;
  onToggleRead?: (chat: DirectChat) => void;
  selectionProps?: TableSelectionProps<DirectChat>;
  getCompanyHref?: (chat: DirectChat) => string | undefined;
}

export default function DirectMessagesTable({
  directMessages,
  onOpenChat,
  onToggleRead,
  selectionProps,
  getCompanyHref,
}: DirectMessagesTableProps) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  if (!directMessages.length) {
    return (
      <Text c="dimmed" ta="center" py="md" size="sm">
        No new messages.
      </Text>
    );
  }

  if (isMobile) {
    return (
      <Stack gap={0} w="100%">
        {directMessages.map((msg, index) => (
          <div key={msg.chatId}>
            {index > 0 && <Divider />}
            <InboxMessageListItem
              avatarUrl={msg.companyLogoUrl}
              name={msg.companyName}
              subtitle={msg.lastMessageAuthorName}
              timestamp={formatTimeOrDateNow(msg.lastMessageDateTime)}
              hasUnread={msg.unreadMessages > 0}
              onClick={() => onOpenChat?.(msg)}
              onToggleRead={onToggleRead ? () => onToggleRead(msg) : undefined}
            />
          </div>
        ))}
      </Stack>
    );
  }

  return (
    <Table highlightOnHover w="100%" style={{ tableLayout: 'fixed' }}>
      <Table.Thead>
        <Table.Tr>
          {selectionProps && (
            <Table.Th style={TABLE_HEADER_STYLE} w={40}>
              <Checkbox
                size="sm"
                checked={selectionProps.allSelected}
                indeterminate={selectionProps.someSelected}
                onChange={selectionProps.onToggleAll}
                aria-label="Select all direct messages"
              />
            </Table.Th>
          )}
          <Table.Th style={TABLE_HEADER_STYLE} w={200}>
            Chat name
          </Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE} w={180}>
            Last sender
          </Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE}>Subject</Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE} ta="right" w={150} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {directMessages.map((msg) => {
          const hasUnread = msg.unreadMessages > 0;
          const isHovered = hoveredId === msg.chatId;
          const companyHref = isAnnouncementChat(msg)
            ? undefined
            : getCompanyHref?.(msg);
          const companyLabel = msg.companyName;

          return (
            <Table.Tr
              key={msg.chatId}
              style={{ cursor: 'pointer' }}
              fw={hasUnread ? 700 : 400}
              onClick={() => onOpenChat?.(msg)}
              onMouseEnter={() => setHoveredId(msg.chatId)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {selectionProps && (
                <Table.Td w={40}>
                  <Checkbox
                    size="sm"
                    checked={selectionProps.selectedIds.has(
                      selectionProps.getId(msg)
                    )}
                    onChange={() => selectionProps.onToggle(msg)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${msg.companyName}`}
                  />
                </Table.Td>
              )}
              <Table.Td fz={14}>
                {companyHref ? (
                  <Anchor
                    href={companyHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    underline="always"
                    c="inherit"
                    fw="inherit"
                    size="sm"
                  >
                    {companyLabel}
                  </Anchor>
                ) : (
                  companyLabel
                )}
              </Table.Td>
              <Table.Td fz={14}>
                <Text lineClamp={1} size="sm" fw="inherit">
                  {msg.lastMessageAuthorName}
                </Text>
              </Table.Td>
              <Table.Td fz={14}>
                <Text lineClamp={1} size="sm" fw="inherit">
                  {msg.subject}
                </Text>
              </Table.Td>
              <Table.Td ta="right" fz={14}>
                <Flex align="center" justify="flex-end" gap={8} mih={30}>
                  {isHovered ? (
                    <>
                      {onToggleRead && (
                        <Tooltip
                          label={`Mark as ${hasUnread ? 'read' : 'unread'}`}
                          position="top"
                        >
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleRead(msg);
                            }}
                          >
                            <MailIcon
                              hasUnread={hasUnread}
                              isHovered={isHovered}
                            />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Button
                        size="compact-sm"
                        fw={700}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenChat?.(msg);
                        }}
                      >
                        Open
                      </Button>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {formatLocalDate(
                        msg.lastMessageDateTime,
                        DATE_FORMATS.SHORT_DATE_YEAR_FORMAT
                      )}
                    </Text>
                  )}
                </Flex>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
